import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHeroSlides } from '../services/wordpress';
import { HeroSlide } from '../types';
import { ArrowRight, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { safeHTML } from '../utils/safeHTML';
import './Hero.css';

const Hero: React.FC = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchSlides() {
      const data = await getHeroSlides();
      setSlides(data);
      setLoading(false);
    }
    fetchSlides();
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || isPaused) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length, isPaused]);

  if (loading) {
    return (
      <section className="Hero_hero" style={{ backgroundColor: '#0f172a' }}>
        <div className="Hero_contentWrapper container" style={{ zIndex: 1, height: '100%' }}>
          <div className="Hero_contentInner" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '800px' }}>
            <div style={{ width: '120px', height: '32px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '9999px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            <div style={{ width: '90%', height: '64px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            <div style={{ width: '70%', height: '64px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            <div style={{ width: '80%', height: '24px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite', marginTop: '1rem' }} />
            <div style={{ width: '60%', height: '24px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <div style={{ width: '160px', height: '48px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
              <div style={{ width: '160px', height: '48px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            </div>
          </div>
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
      </section>
    );
  }

  if (slides.length === 0) return null;

  return (
    <section 
      className="Hero_hero"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`Hero_slide ${index === currentSlide ? 'Hero_slideActive' : ''}`}
        >
          <div className="Hero_bgImageWrapper">
            <img src={slide.imageUrl} alt={slide.title} className="Hero_bgImage" width="1920" height="1080" />
            <div className="Hero_overlayGradient" />
            <div className="Hero_overlayBottom" />
          </div>

          <div className={`Hero_contentWrapper container`}>
            <div className="Hero_contentInner">
              <div className="Hero_categoryTag">
                <span className="Hero_dot"></span>
                {slide.category}
              </div>
              
              {slide.isFallback && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#fcd34d', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 600, border: '1px solid rgba(245, 158, 11, 0.3)', marginBottom: '1rem' }}>
                  <AlertTriangle size={16} /> Mode Offline
                </div>
              )}

              <h1 className="Hero_title">{slide.title}</h1>

              <div className="Hero_subtitle" {...safeHTML(slide.subtitle)} />

              <div className="Hero_buttonGroup">
                <button onClick={() => navigate(`/${slide.slug || slide.id}`)} className="Hero_btnPrimary">
                  Selengkapnya <ArrowRight size={20} />
                </button>
                <button onClick={() => navigate('/riset')} className="Hero_btnSecondary">
                  Jelajahi Riset
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {slides.length > 1 && (
        <div className="Hero_controls">
            <button onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)} className="Hero_controlBtn" aria-label="Slide sebelumnya">
              <ChevronLeft size={24} />
            </button>
            <div className="Hero_indicators" role="tablist" aria-label="Navigasi slide">
                {slides.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`Hero_indicator ${idx === currentSlide ? 'Hero_indicatorActive' : ''}`}
                        role="tab"
                        aria-selected={idx === currentSlide}
                        aria-label={`Slide ${idx + 1} dari ${slides.length}`}
                    />
                ))}
            </div>
            <button onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)} className="Hero_controlBtn" aria-label="Slide berikutnya">
              <ChevronRight size={24} />
            </button>
        </div>
      )}
    </section>
  );
};

export default Hero;

